/**
 * Latency Presets
 *
 * User-facing latency configuration for audio timing.
 * Controls the look-ahead window for the AudioScheduler
 * and the latency compensation for MIDI clock.
 */

import { getPlatform } from '~/lib/platform';

export type LatencyPreset = 'low' | 'balanced' | 'safe';

export interface LatencyConfig {
  /** User-facing label */
  label: string;
  /** Description for UI */
  description: string;
  /** Look-ahead in seconds for the AudioScheduler */
  lookAheadSeconds: number;
  /** MIDI latency compensation in ms */
  midiLatencyMs: number;
}

/**
 * Get platform-specific latency presets
 */
export function getLatencyPresets(): Record<LatencyPreset, LatencyConfig> {
  const platform = getPlatform();

  switch (platform) {
    case 'ios':
      return {
        low: {
          label: 'Low',
          description: 'Tightest timing, may glitch on older devices',
          lookAheadSeconds: 0.03,
          midiLatencyMs: 0,
        },
        balanced: {
          label: 'Balanced',
          description: 'Good timing with stable playback',
          lookAheadSeconds: 0.05,
          midiLatencyMs: 5,
        },
        safe: {
          label: 'Safe',
          description: 'Most stable, slightly higher latency',
          lookAheadSeconds: 0.08,
          midiLatencyMs: 10,
        },
      };

    case 'android':
      return {
        low: {
          label: 'Low',
          description: 'Tightest timing, may glitch on some devices',
          lookAheadSeconds: 0.05,
          midiLatencyMs: 5,
        },
        balanced: {
          label: 'Balanced',
          description: 'Good timing with stable playback',
          lookAheadSeconds: 0.08,
          midiLatencyMs: 10,
        },
        safe: {
          label: 'Safe',
          description: 'Most stable, higher latency for compatibility',
          lookAheadSeconds: 0.12,
          midiLatencyMs: 15,
        },
      };

    default: // web / desktop
      return {
        low: {
          label: 'Low',
          description: 'Tightest timing for low-latency setups',
          lookAheadSeconds: 0.02,
          midiLatencyMs: 0,
        },
        balanced: {
          label: 'Balanced',
          description: 'Good timing with stable playback',
          lookAheadSeconds: 0.03,
          midiLatencyMs: 3,
        },
        safe: {
          label: 'Safe',
          description: 'Most stable for high-load scenarios',
          lookAheadSeconds: 0.06,
          midiLatencyMs: 8,
        },
      };
  }
}

/**
 * Get the default latency preset for the current platform
 */
export function getDefaultLatencyPreset(): LatencyPreset {
  return 'balanced';
}

/**
 * Get latency config for a preset
 */
export function getLatencyConfig(preset: LatencyPreset): LatencyConfig {
  const presets = getLatencyPresets();
  return presets[preset];
}

/**
 * All available preset keys
 */
export const LATENCY_PRESET_KEYS: LatencyPreset[] = ['low', 'balanced', 'safe'];
